import { PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS } from '../api/peralatanToParameterUji';
import type { DynamicEntityRow } from '../api/strapiDynamicEntity';

export type MasterFormField =
  | {
      attribute: string;
      kind: 'text' | 'email';
      label: string;
      required?: boolean;
      multiline?: boolean;
      helperText?: string;
    }
  | {
      attribute: string;
      kind: 'number';
      label: string;
      required?: boolean;
      step?: string;
      helperText?: string;
      visibleWhen?: { attribute: string; equals: string };
    }
  | { attribute: string; kind: 'boolean'; label: string }
  | {
      attribute: string;
      kind: 'enumeration';
      label: string;
      options: readonly string[];
      required?: boolean;
    }
  | { attribute: string; kind: 'json'; label: string; required?: boolean; helperText?: string }
  | {
      attribute: string;
      kind: 'relation';
      label: string;
      relation:
        | 'customer'
        | 'quotation'
        | 'sample_information'
        | 'sample_information_point'
        | 'test_result'
        | 'variable_environment'
        | 'user'
        | 'grouping'
        | 'jenis_pengukuran'
        | 'form_question'
        | 'baku_mutu'
        | 'parameter_uji'
        | 'sni_method'
        | 'user_type'
        | 'analis_type_data'
        /** Select any referensi row; maps to payload field via `referensi_field`. */
        | 'referensi_fk';
      /** Payload attribute on the Strapi entity (e.g. `kategori_referensi`, tree parent uses `grouping`). */
      referensi_field?:
        | 'kategori_referensi'
        | 'sub_kategori_referensi'
        | 'merk_referensi'
        | 'larutan_penjerap'
        | 'cara_pengambilan_sample'
        | 'grouping';
      required?: boolean;
    }
  | {
      attribute: string;
      kind: 'relation_many';
      label: string;
      /** Strapi many-to-many field on the entity. */
      payload_field:
        | 'perlengkapan_pengambilan_contoh'
        | 'perlengkapan_pengukuran_lapangan'
        | 'perlengkapan_pendukung'
        | 'perlengkapan_k3'
        | 'jenis_pengukuran'
        | 'form_question';
      kode_kategori?: string;
      kode_kategori_mode?: 'exact' | 'prefix';
      required?: boolean;
    };

export type MasterGridColumn = {
  field: string;
  headerName: string;
  flex?: number;
  minWidth?: number;
  valueGetter?: (row: DynamicEntityRow) => string;
};

export type MasterCrudRegistryEntry = {
  title: string;
  description?: string;
  apiPlural: string;
  populate: string;
  fixedListFilters?: Partial<Record<string, string>>;
  exactListFilters?: Partial<Record<string, string>>;
  createDefaults?: Record<string, unknown>;
  columns: MasterGridColumn[];
  formFields: MasterFormField[];
};

export type ReferensiMasterGroup = {
  slug: string;
  title: string;
  kodeKategori: string;
  description?: string;
};

export function slugFromReferensiKodeKategori(kodeKategori: string): string {
  return `referensi-${kodeKategori.toLowerCase().replace(/_/g, '-')}`;
}

export function kodeKategoriFromReferensiSlug(slug: string): string | null {
  const prefix = 'referensi-';
  if (!slug.startsWith(prefix)) return null;
  const raw = slug.slice(prefix.length).trim();
  if (!raw) return null;
  return raw.toUpperCase().replace(/-/g, '_');
}

export function labelFromReferensiKodeKategori(kodeKategori: string): string {
  return kodeKategori
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => (word === 'k3' ? 'K-3' : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

function relationRecord(v: unknown): Record<string, unknown> | null {
  if (v == null || typeof v !== 'object') return null;
  if (Array.isArray(v)) return null;
  if ('data' in v) {
    const d = (v as { data: unknown }).data;
    if (d && typeof d === 'object' && !Array.isArray(d)) return relationRecord(d);
    return null;
  }
  const rec = v as Record<string, unknown>;
  const attrs = rec.attributes;
  if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
    return {
      ...(attrs as Record<string, unknown>),
      documentId: rec.documentId ?? (attrs as Record<string, unknown>).documentId,
    };
  }
  return rec;
}

function stringifyCell(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    return v.map(stringifyCell).filter(Boolean).join(', ');
  }
  if (typeof v === 'object') {
    const o = relationRecord(v) ?? (v as Record<string, unknown>);
    return String(
      o.nama_parameter ??
        o.nama_baku_mutu ??
        o.nama_jenis ??
        o.nama_grouping ??
        o.name ??
        o.title ??
        o.quotation_number ??
        o.documentId ??
        '',
    );
  }
  return String(v);
}

function bakuMutuCellLabel(v: unknown): string {
  const o = relationRecord(v);
  if (!o) return stringifyCell(v);
  const k = String(o.kode ?? '').trim();
  const n = String(o.nama_baku_mutu ?? '').trim();
  return [k, n].filter(Boolean).join(' — ') || stringifyCell(v);
}

function parameterUjiCellLabel(v: unknown): string {
  const o = relationRecord(v);
  if (!o) return stringifyCell(v);
  const n = String(o.nama_parameter ?? '').trim();
  const s = String(o.simbol ?? '').trim();
  return [n, s].filter(Boolean).join(' ') || stringifyCell(v);
}

function asRelationList(v: unknown): Record<string, unknown>[] {
  if (v == null) return [];
  if (Array.isArray(v)) {
    return v.map(relationRecord).filter((row): row is Record<string, unknown> => row != null);
  }
  if (typeof v === 'object' && Array.isArray((v as { data?: unknown }).data)) {
    return ((v as { data: unknown[] }).data)
      .map(relationRecord)
      .filter((row): row is Record<string, unknown> => row != null);
  }
  const one = relationRecord(v);
  return one ? [one] : [];
}

function sniMethodCellLabel(v: unknown): string {
  const o = relationRecord(v);
  if (!o) return stringifyCell(v);
  const kode = String(o.kode_sni ?? '').trim();
  const judul = String(o.judul ?? '').trim();
  return [kode, judul].filter(Boolean).join(' — ') || stringifyCell(v);
}

function metodaUjiLabelFromMetodaRow(m: Record<string, unknown>): string {
  const fromRel = sniMethodCellLabel(m.sni_method_id);
  if (fromRel) return fromRel;
  return String(m.kode_sni ?? '').trim();
}

function parameterUjiMetodeUjiCellLabel(metodas: unknown): string {
  return asRelationList(metodas)
    .map((m) => {
      const label = metodaUjiLabelFromMetodaRow(m);
      if (!label) return '';
      return m.is_default ? `${label} (default)` : label;
    })
    .filter(Boolean)
    .join(', ');
}

const REFERENSI_GROUP_COLUMNS: MasterGridColumn[] = [
  { field: 'kode', headerName: 'Kode', flex: 0.16 },
  {
    field: 'parent',
    headerName: 'Parent',
    flex: 0.35,
    valueGetter: (r) => stringifyCell((r as { grouping?: unknown }).grouping),
  },
  {
    field: 'deskripsi1',
    headerName: 'Deskripsi',
    flex: 0.6,
    valueGetter: (r) => String((r.deskripsi1 ?? '') as string).slice(0, 120),
  },
  { field: 'deskripsi2', headerName: 'Deskripsi 2', flex: 0.45 },
  { field: 'active_status', headerName: 'Status', flex: 0.12 },
  { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
];

const REFERENSI_GROUP_FORM_FIELDS: MasterFormField[] = [
  { attribute: 'kode', kind: 'text', label: 'Kode' },
  {
    attribute: 'grouping_doc',
    kind: 'relation',
    relation: 'referensi_fk',
    referensi_field: 'grouping',
    label: 'Referensi induk (pohon)',
  },
  { attribute: 'deskripsi1', kind: 'text', label: 'Deskripsi 1' },
  { attribute: 'deskripsi2', kind: 'text', label: 'Deskripsi 2' },
  { attribute: 'active_status', kind: 'number', label: 'Status' },
];

export function createReferensiMasterGroupEntry(group: ReferensiMasterGroup): MasterCrudRegistryEntry {
  return {
    title: `Master — ${group.title}`,
    description: group.description ?? `Kode kategori otomatis: ${group.kodeKategori}.`,
    apiPlural: 'referensis',
    populate: '*',
    exactListFilters: { kode_kategori: group.kodeKategori },
    createDefaults: { kode_kategori: group.kodeKategori },
    columns: REFERENSI_GROUP_COLUMNS,
    formFields: REFERENSI_GROUP_FORM_FIELDS,
  };
}

export const MASTER_CRUD_REGISTRY: Record<string, MasterCrudRegistryEntry> = {
  'sample-detail-attachments': {
    title: 'Sample attachments',
    description: 'Photo URL tied to a sample titik (`sample_information_point`).',
    apiPlural: 'sample-detail-attachments',
    populate: '*',
    columns: [
      {
        field: 'sample',
        headerName: 'Sample',
        flex: 0.55,
        valueGetter: (r) =>
          stringifyCell(r.sample_information_point) ||
          String((r.sample_information_point as { documentId?: string })?.documentId ?? ''),
      },
      {
        field: 'picture',
        headerName: 'Picture',
        flex: 0.6,
        valueGetter: (r) => String(r.picture ?? ''),
      },
      {
        field: 'is_main_picture',
        headerName: 'Main',
        flex: 0.2,
        valueGetter: (r) => stringifyCell(r.is_main_picture),
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'sample_information_point_doc',
        kind: 'relation',
        relation: 'sample_information_point',
        label: 'Sample titik',
        required: true,
      },
      { attribute: 'picture', kind: 'text', label: 'Picture (URL or path)', required: true },
      { attribute: 'is_main_picture', kind: 'boolean', label: 'Main picture' },
    ],
  },
  'sample-detail-surveyors': {
    title: 'Sample surveyors',
    apiPlural: 'sample-detail-surveyors',
    populate: '*',
    columns: [
      {
        field: 'sample',
        headerName: 'Sample',
        flex: 0.55,
        valueGetter: (r) =>
          stringifyCell(r.sample_information_point) ||
          String((r.sample_information_point as { documentId?: string })?.documentId ?? ''),
      },
      {
        field: 'user',
        headerName: 'User',
        flex: 0.5,
        valueGetter: (r) => {
          const u = r.user as Record<string, unknown> | undefined;
          if (!u || typeof u !== 'object') return '';
          return `${String(u.username ?? '')} · ${String(u.email ?? '')}`.trim();
        },
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'sample_information_point_doc',
        kind: 'relation',
        relation: 'sample_information_point',
        label: 'Sample titik',
        required: true,
      },
      {
        attribute: 'user_staff',
        kind: 'relation',
        relation: 'user',
        label: 'Surveyor user',
        required: true,
      },
    ],
  },
  'test-results': {
    title: 'Test result lines',
    description: 'Analytical result row linked to a sample (parent for environmental rows and remarks).',
    apiPlural: 'test-results',
    populate: '*',
    columns: [
      {
        field: 'sample',
        headerName: 'Sample',
        flex: 0.55,
        valueGetter: (r) =>
          stringifyCell(r.sample_information_point) ||
          String((r.sample_information_point as { documentId?: string })?.documentId ?? ''),
      },
      { field: 'test_item', headerName: 'Test item', flex: 0.4 },
      { field: 'test_method', headerName: 'Method', flex: 0.4 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'sample_information_point_doc',
        kind: 'relation',
        relation: 'sample_information_point',
        label: 'Sample titik',
        required: true,
      },
      { attribute: 'test_item', kind: 'text', label: 'Test item', required: true },
      { attribute: 'test_method', kind: 'text', label: 'Test method', required: true },
      { attribute: 'unit', kind: 'text', label: 'Unit' },
      { attribute: 'result', kind: 'text', label: 'Result' },
    ],
  },
  'test-result-environment-conditions': {
    title: 'Test result environmental rows',
    apiPlural: 'test-result-environment-conditions',
    populate: '*',
    columns: [
      {
        field: 'environment_variable',
        headerName: 'Condition variable',
        flex: 0.45,
        valueGetter: (r) => stringifyCell(r.environment_variable),
      },
      {
        field: 'test_result',
        headerName: 'Test result',
        flex: 0.4,
        valueGetter: (r) => stringifyCell(r.test_result),
      },
      { field: 'result_value', headerName: 'Value', flex: 0.35 },
      { field: 'unit', headerName: 'Unit', flex: 0.25 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'environment_variable_doc',
        kind: 'relation',
        relation: 'variable_environment',
        label: 'Environmental condition variable',
        required: true,
      },
      {
        attribute: 'test_result_doc',
        kind: 'relation',
        relation: 'test_result',
        label: 'Test result',
        required: true,
      },
      { attribute: 'result_value', kind: 'text', label: 'Result value' },
      {
        attribute: 'result_type',
        kind: 'enumeration',
        label: 'Result type',
        options: ['int', 'string', 'float'],
      },
      { attribute: 'unit', kind: 'text', label: 'Unit' },
    ],
  },
  'test-result-remarks': {
    title: 'Test result remarks',
    apiPlural: 'test-result-remarks',
    populate: '*',
    columns: [
      {
        field: 'test_result',
        headerName: 'Test result',
        flex: 0.45,
        valueGetter: (r) => stringifyCell(r.test_result),
      },
      {
        field: 'note',
        headerName: 'Note',
        flex: 1,
        valueGetter: (r) => String(r.note ?? '').slice(0, 120),
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'test_result_doc',
        kind: 'relation',
        relation: 'test_result',
        label: 'Test result',
        required: true,
      },
      { attribute: 'items', kind: 'text', label: 'Items (HTML / long text)', multiline: true },
      { attribute: 'note', kind: 'text', label: 'Note', multiline: true },
    ],
  },
  'variable-pengujiens': {
    title: 'Variable — pengujian',
    apiPlural: 'variable-pengujiens',
    populate: '*',
    columns: [
      { field: 'name', headerName: 'Name', flex: 0.5 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 0.8,
        valueGetter: (r) => String(r.description ?? '').slice(0, 120),
      },
      { field: 'created_by_email', headerName: 'Created by', flex: 0.35 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'name', kind: 'text', label: 'Name', required: true },
      { attribute: 'description', kind: 'text', label: 'Description', multiline: true },
      { attribute: 'created_by_email', kind: 'email', label: 'Created by (email)' },
    ],
  },
  'variable-units': {
    title: 'Variable — units',
    apiPlural: 'variable-units',
    populate: '*',
    columns: [
      { field: 'name', headerName: 'Name', flex: 0.5 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 0.8,
        valueGetter: (r) => String(r.description ?? '').slice(0, 120),
      },
      { field: 'created_by_email', headerName: 'Created by', flex: 0.35 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'name', kind: 'text', label: 'Name', required: true },
      { attribute: 'description', kind: 'text', label: 'Description', multiline: true },
      { attribute: 'created_by_email', kind: 'email', label: 'Created by (email)' },
    ],
  },
  'variable-test-methods': {
    title: 'Variable — test methods',
    apiPlural: 'variable-test-methods',
    populate: '*',
    columns: [
      { field: 'name', headerName: 'Name', flex: 0.5 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 0.8,
        valueGetter: (r) => String(r.description ?? '').slice(0, 120),
      },
      { field: 'created_by_email', headerName: 'Created by', flex: 0.35 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'name', kind: 'text', label: 'Name', required: true },
      { attribute: 'description', kind: 'text', label: 'Description', multiline: true },
      { attribute: 'created_by_email', kind: 'email', label: 'Created by (email)' },
    ],
  },
  'variable-environment-conditions': {
    title: 'Variable — environmental conditions',
    apiPlural: 'variable-environment-conditions',
    populate: '*',
    columns: [
      { field: 'name', headerName: 'Name', flex: 0.5 },
      {
        field: 'description',
        headerName: 'Description',
        flex: 0.8,
        valueGetter: (r) => String(r.description ?? '').slice(0, 120),
      },
      { field: 'created_by_email', headerName: 'Created by', flex: 0.35 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'name', kind: 'text', label: 'Name', required: true },
      { attribute: 'description', kind: 'text', label: 'Description', multiline: true },
      { attribute: 'created_by_email', kind: 'email', label: 'Created by (email)' },
    ],
  },

  /** --- Lab masters (legacy MySQL → Strapi) --- */
  groupings: {
    title: 'Master — Grouping',
    apiPlural: 'groupings',
    populate: '*',
    columns: [
      { field: 'nama_grouping', headerName: 'Nama', flex: 0.55 },
      { field: 'air', headerName: 'Air', flex: 0.12, valueGetter: (r) => stringifyCell(r.air) },
      {
        field: 'perencanaan_sampling',
        headerName: 'Perencanaan sampling',
        flex: 0.22,
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'nama_grouping', kind: 'text', label: 'Nama grouping', required: true },
      {
        attribute: 'perencanaan_sampling',
        kind: 'number',
        label: 'Perencanaan sampling (v1: 1=air, 2=udara, 3=LB3)',
      },
      { attribute: 'air', kind: 'boolean', label: 'Air (legacy, tidak dipakai v2)' },
    ],
  },
  'jenis-pengukurans': {
    title: 'Master — Jenis pengukuran',
    apiPlural: 'jenis-pengukurans',
    populate: '*',
    columns: [
      {
        field: 'grouping',
        headerName: 'Grouping',
        flex: 0.35,
        valueGetter: (r) => stringifyCell(r.grouping),
      },
      { field: 'nama_jenis', headerName: 'Nama', flex: 0.35 },
      { field: 'kode_jenis', headerName: 'Kode', flex: 0.2 },
      { field: 'active_status', headerName: 'Status', flex: 0.12 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'grouping_doc',
        kind: 'relation',
        relation: 'grouping',
        label: 'Grouping',
        required: true,
      },
      { attribute: 'nama_jenis', kind: 'text', label: 'Nama jenis', required: true },
      { attribute: 'kode_jenis', kind: 'text', label: 'Kode jenis', required: true },
      { attribute: 'active_status', kind: 'number', label: 'Status' },
    ],
  },
  'baku-mutus': {
    title: 'Master — Baku mutu',
    apiPlural: 'baku-mutus',
    populate: '*',
    columns: [
      { field: 'kode', headerName: 'Kode', flex: 0.18 },
      { field: 'nama_baku_mutu', headerName: 'Nama', flex: 0.35 },
      { field: 'judul', headerName: 'Judul', flex: 0.35 },
      {
        field: 'grouping',
        headerName: 'Grouping',
        flex: 0.25,
        valueGetter: (r) => stringifyCell(r.grouping),
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'kode', kind: 'text', label: 'Kode', required: true },
      { attribute: 'nama_baku_mutu', kind: 'text', label: 'Nama baku mutu', required: true },
      { attribute: 'judul', kind: 'text', label: 'Judul', required: true },
      {
        attribute: 'grouping_doc',
        kind: 'relation',
        relation: 'grouping',
        label: 'Grouping',
      },
      {
        attribute: 'jenis_pengukuran_doc',
        kind: 'relation',
        relation: 'jenis_pengukuran',
        label: 'Jenis pengukuran',
      },
      { attribute: 'lampiran', kind: 'text', label: 'Lampiran' },
      { attribute: 'nab_bml', kind: 'text', label: 'NAB/BML' },
      { attribute: 'catatan', kind: 'text', label: 'Catatan', multiline: true },
    ],
  },
  referensis: {
    title: 'Master — Referensi',
    description: 'Parent row uses relation field `grouping` in Strapi.',
    apiPlural: 'referensis',
    populate: '*',
    columns: [
      { field: 'kode_kategori', headerName: 'Kode kategori', flex: 0.22 },
      { field: 'kode', headerName: 'Kode', flex: 0.15 },
      {
        field: 'parent',
        headerName: 'Parent',
        flex: 0.35,
        valueGetter: (r) => stringifyCell((r as { grouping?: unknown }).grouping),
      },
      {
        field: 'deskripsi1',
        headerName: 'Deskripsi',
        flex: 0.45,
        valueGetter: (r) => String((r.deskripsi1 ?? '') as string).slice(0, 80),
      },
      { field: 'active_status', headerName: 'Status', flex: 0.1 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'kode_kategori', kind: 'text', label: 'Kode kategori', required: true },
      { attribute: 'kode', kind: 'text', label: 'Kode' },
      {
        attribute: 'grouping_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'grouping',
        label: 'Referensi induk (pohon)',
      },
      { attribute: 'deskripsi1', kind: 'text', label: 'Deskripsi 1' },
      { attribute: 'deskripsi2', kind: 'text', label: 'Deskripsi 2' },
      { attribute: 'active_status', kind: 'number', label: 'Status' },
    ],
  },
  'parameter-ujis': {
    title: 'Master — Parameter uji',
    apiPlural: 'parameter-ujis',
    populate: '*',
    columns: [
      { field: 'nama_parameter', headerName: 'Parameter', flex: 0.4 },
      { field: 'simbol', headerName: 'Simbol', flex: 0.12 },
      {
        field: 'metode_uji',
        headerName: 'Metode uji',
        flex: 0.45,
        valueGetter: (r) =>
          parameterUjiMetodeUjiCellLabel((r as { parameter_uji_metodas?: unknown }).parameter_uji_metodas),
      },
      {
        field: 'kategori',
        headerName: 'Kategori ref.',
        flex: 0.35,
        valueGetter: (r) => stringifyCell((r as { kategori_referensi?: unknown }).kategori_referensi),
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'nama_parameter', kind: 'text', label: 'Nama parameter', required: true },
      { attribute: 'simbol', kind: 'text', label: 'Simbol' },
      {
        attribute: 'kategori_referensi_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'kategori_referensi',
        label: 'Kategori (referensi)',
      },
      { attribute: 'akomodasi', kind: 'text', label: 'Akomodasi', required: true },
      { attribute: 'data', kind: 'text', label: 'Data', required: true },
      { attribute: 'beban_kerja', kind: 'text', label: 'Beban kerja', required: true },
      { attribute: 'volume_minimum', kind: 'text', label: 'Volume minimum' },
      { attribute: 'pengawetan', kind: 'number', label: 'Pengawetan' },
      { attribute: 'batas_penyimpanan', kind: 'text', label: 'Batas penyimpanan' },
      {
        attribute: 'larutan_penjerap_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'larutan_penjerap',
        label: 'Larutan penjerap (referensi)',
      },
      {
        attribute: 'cara_pengambilan_sample_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'cara_pengambilan_sample',
        label: 'Cara pengambilan sampel (referensi)',
      },
    ],
  },
  'parameter-uji-metodas': {
    title: 'Master — Parameter uji metoda',
    description: 'Metoda / SNI / satuan per parameter uji.',
    apiPlural: 'parameter-uji-metodas',
    populate: '*',
    columns: [
      {
        field: 'parameter_uji',
        headerName: 'Parameter uji',
        flex: 0.35,
        valueGetter: (r) => parameterUjiCellLabel((r as { parameter_uji?: unknown }).parameter_uji),
      },
      {
        field: 'metode_uji',
        headerName: 'Metode uji',
        flex: 0.45,
        valueGetter: (r) => {
          const fromRel = sniMethodCellLabel((r as { sni_method_id?: unknown }).sni_method_id);
          if (fromRel) return fromRel;
          return String((r as { kode_sni?: unknown }).kode_sni ?? '').trim();
        },
      },
      { field: 'satuan', headerName: 'Satuan', flex: 0.15 },
      {
        field: 'is_default',
        headerName: 'Default',
        flex: 0.12,
        valueGetter: (r) => stringifyCell(r.is_default),
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'parameter_uji_doc',
        kind: 'relation',
        relation: 'parameter_uji',
        label: 'Parameter uji',
        required: true,
      },
      {
        attribute: 'sni_method_doc',
        kind: 'relation',
        relation: 'sni_method',
        label: 'Metode uji (SNI)',
      },
      {
        attribute: 'kode_sni',
        kind: 'text',
        label: 'Kode SNI',
        helperText: 'Terisi otomatis dari metode uji yang dipilih; boleh diubah manual.',
      },
      { attribute: 'sni_id', kind: 'number', label: 'SNI id (legacy)' },
      { attribute: 'alat_id', kind: 'number', label: 'Alat id (legacy)' },
      { attribute: 'satuan', kind: 'text', label: 'Satuan' },
      { attribute: 'loq', kind: 'number', label: 'LOQ', step: 'any' },
      { attribute: 'is_default', kind: 'boolean', label: 'Metoda default' },
    ],
  },
  'sni-methods': {
    title: 'Master — SNI methods',
    description: 'Master metode SNI hasil migrasi legacy MySQL `sni`.',
    apiPlural: 'sni-methods',
    populate: '*',
    columns: [
      { field: 'kode_sni', headerName: 'Kode SNI', flex: 0.28 },
      {
        field: 'judul',
        headerName: 'Judul',
        flex: 0.7,
        valueGetter: (r) => String((r.judul ?? '') as string).slice(0, 140),
      },
      {
        field: 'kategori_referensi',
        headerName: 'Kategori',
        flex: 0.35,
        valueGetter: (r) => stringifyCell((r as { kategori_referensi?: unknown }).kategori_referensi),
      },
      { field: 'lampiran', headerName: 'Lampiran', flex: 0.2 },
      { field: 'legacy_mysql_sni_id', headerName: 'Legacy ID', flex: 0.14 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'kode_sni', kind: 'text', label: 'Kode SNI', required: true },
      { attribute: 'judul', kind: 'text', label: 'Judul', required: true, multiline: true },
      { attribute: 'lampiran', kind: 'text', label: 'Lampiran' },
      {
        attribute: 'kategori_referensi_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'kategori_referensi',
        label: 'Kategori (referensi)',
      },
      { attribute: 'kategori_kode', kind: 'text', label: 'Kode kategori' },
      { attribute: 'sorting', kind: 'number', label: 'Sorting' },
      { attribute: 'legacy_mysql_sni_id', kind: 'number', label: 'SNI id (legacy)' },
    ],
  },
  'freelance-users': {
    title: 'Master — Freelance users',
    description:
      'Petugas sampling placeholder tanpa akun login, dipakai saat penjadwalan sampling.',
    apiPlural: 'freelance-users',
    populate: '*',
    createDefaults: {
      active_status: 'active',
    },
    columns: [
      { field: 'name', headerName: 'Nama', flex: 0.35 },
      { field: 'phone_number', headerName: 'No. HP', flex: 0.22 },
      {
        field: 'active_status',
        headerName: 'Status',
        flex: 0.12,
        valueGetter: (r) => stringifyCell(r.active_status),
      },
      {
        field: 'user_type',
        headerName: 'Tipe',
        flex: 0.25,
        valueGetter: (r) => {
          const ut = (r as { user_type?: unknown }).user_type;
          if (!ut || typeof ut !== 'object') return '';
          const o = ut as Record<string, unknown>;
          return String(o.type_name ?? o.type_code ?? '').trim();
        },
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'name', kind: 'text', label: 'Nama', required: true },
      { attribute: 'phone_number', kind: 'text', label: 'Nomor HP' },
      {
        attribute: 'active_status',
        kind: 'enumeration',
        label: 'Status',
        options: ['active', 'inactive'] as const,
        required: true,
      },
      {
        attribute: 'user_type_doc',
        kind: 'relation',
        relation: 'user_type',
        label: 'Tipe user',
        required: true,
      },
    ],
  },
  tools: {
    title: 'Master — Tools',
    description: 'Master alat laboratorium hasil migrasi legacy MySQL `alat`.',
    apiPlural: 'tools',
    populate: '*',
    columns: [
      { field: 'nama_alat', headerName: 'Nama alat', flex: 0.45 },
      { field: 'nomor_inventaris_alat', headerName: 'No. inventaris', flex: 0.35 },
      {
        field: 'kategori_referensi',
        headerName: 'Kategori',
        flex: 0.28,
        valueGetter: (r) => stringifyCell((r as { kategori_referensi?: unknown }).kategori_referensi),
      },
      {
        field: 'merk_referensi',
        headerName: 'Merk',
        flex: 0.24,
        valueGetter: (r) => stringifyCell((r as { merk_referensi?: unknown }).merk_referensi),
      },
      {
        field: 'is_active',
        headerName: 'Active',
        flex: 0.12,
        valueGetter: (r) => stringifyCell(r.is_active),
      },
      { field: 'legacy_mysql_alat_id', headerName: 'Legacy ID', flex: 0.14 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'nama_alat', kind: 'text', label: 'Nama alat', required: true },
      {
        attribute: 'nomor_inventaris_alat',
        kind: 'text',
        label: 'Nomor inventaris alat',
        required: true,
      },
      {
        attribute: 'kategori_referensi_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'kategori_referensi',
        label: 'Kategori (referensi)',
      },
      {
        attribute: 'sub_kategori_referensi_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'sub_kategori_referensi',
        label: 'Sub kategori (referensi)',
      },
      {
        attribute: 'merk_referensi_doc',
        kind: 'relation',
        relation: 'referensi_fk',
        referensi_field: 'merk_referensi',
        label: 'Merk (referensi)',
      },
      { attribute: 'tanggal_kalibrasi', kind: 'text', label: 'Tanggal kalibrasi' },
      { attribute: 'status_kalibrasi', kind: 'text', label: 'Status kalibrasi' },
      { attribute: 'tanggal_ujikinerja', kind: 'text', label: 'Tanggal uji kinerja' },
      { attribute: 'status_ujikinerja', kind: 'text', label: 'Status uji kinerja' },
      { attribute: 'keterangan', kind: 'text', label: 'Keterangan', multiline: true },
      { attribute: 'use_backup', kind: 'boolean', label: 'Gunakan backup' },
      { attribute: 'is_active', kind: 'boolean', label: 'Aktif' },
      { attribute: 'legacy_mysql_alat_id', kind: 'number', label: 'Alat id (legacy)' },
      { attribute: 'legacy_backup_alat_id', kind: 'number', label: 'Backup alat id (legacy)' },
    ],
  },
  'baku-mutu-parameter-ujis': {
    title: 'Master — Baku mutu × parameter uji',
    apiPlural: 'baku-mutu-parameter-ujis',
    populate: '*',
    columns: [
      {
        field: 'baku_mutu',
        headerName: 'Baku mutu',
        flex: 0.35,
        valueGetter: (r) => bakuMutuCellLabel((r as { baku_mutu?: unknown }).baku_mutu),
      },
      {
        field: 'parameter_uji',
        headerName: 'Nama parameter',
        flex: 0.35,
        valueGetter: (r) => parameterUjiCellLabel((r as { parameter_uji?: unknown }).parameter_uji),
      },
      {
        field: 'active_status',
        headerName: 'Status',
        flex: 0.15,
      },
      { field: 'harga', headerName: 'Harga', flex: 0.12 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'baku_mutu_doc',
        kind: 'relation',
        relation: 'baku_mutu',
        label: 'Baku mutu',
        required: true,
      },
      {
        attribute: 'parameter_uji_doc',
        kind: 'relation',
        relation: 'parameter_uji',
        label: 'Parameter uji',
        required: true,
      },
      { attribute: 'loq', kind: 'number', label: 'LOQ', step: 'any' },
      { attribute: 'harga', kind: 'number', label: 'Harga', step: 'any' },
      {
        attribute: 'active_status',
        kind: 'text',
        label: 'Status',
        helperText: 'Contoh: TERSEDIA atau TIDAK TERSEDIA',
      },
      { attribute: 'satuan', kind: 'text', label: 'Satuan' },
      { attribute: 'batch_analysis', kind: 'boolean', label: 'Batch analysis' },
      { attribute: 'urutan_sorting', kind: 'number', label: 'Urutan sorting' },
      { attribute: 'kode_sni', kind: 'text', label: 'Kode SNI' },
      { attribute: 'sni_id', kind: 'number', label: 'SNI id' },
      { attribute: 'parameter_type', kind: 'number', label: 'Parameter type' },
    ],
  },
  'jenis-pengukuran-perlengkapans': {
    title: 'Master — Jenis pengukuran × perlengkapan',
    description:
      'Default perlengkapan per jenis pengukuran (dokumen, peralatan, dll.). Mapping ini otomatis dipreload ke checklist rencana pengambilan sampling saat membuat work order.',
    apiPlural: 'jenis-pengukuran-perlengkapans',
    populate: '*',
    columns: [
      {
        field: 'jenis_pengukuran',
        headerName: 'Jenis pengukuran',
        flex: 0.35,
        valueGetter: (r) => stringifyCell((r as { jenis_pengukuran?: unknown }).jenis_pengukuran),
      },
      {
        field: 'perlengkapan_count',
        headerName: 'Jumlah perlengkapan',
        flex: 0.15,
        valueGetter: (r) => {
          const row = r as Record<string, unknown>;
          const count = (field: string) => (Array.isArray(row[field]) ? row[field].length : 0);
          return String(
            count('perlengkapan_pengambilan_contoh') +
              count('perlengkapan_pengukuran_lapangan') +
              count('perlengkapan_pendukung') +
              count('perlengkapan_k3'),
          );
        },
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'jenis_pengukuran_doc',
        kind: 'relation',
        relation: 'jenis_pengukuran',
        label: 'Jenis pengukuran',
        required: true,
      },
      {
        attribute: 'perlengkapan_pengambilan_contoh_docs',
        kind: 'relation_many',
        label: 'Perlengkapan pengambilan contoh',
        payload_field: 'perlengkapan_pengambilan_contoh',
        kode_kategori: 'PERALATAN_SAMPLING',
        kode_kategori_mode: 'prefix',
      },
      {
        attribute: 'perlengkapan_pengukuran_lapangan_docs',
        kind: 'relation_many',
        label: 'Perlengkapan pengukuran lapangan',
        payload_field: 'perlengkapan_pengukuran_lapangan',
        kode_kategori: 'PERALATAN_UKUR_LAP',
      },
      {
        attribute: 'perlengkapan_pendukung_docs',
        kind: 'relation_many',
        label: 'Perlengkapan pendukung',
        payload_field: 'perlengkapan_pendukung',
        kode_kategori: 'PERALATAN_PENDUKUNG',
      },
      {
        attribute: 'perlengkapan_k3_docs',
        kind: 'relation_many',
        label: 'Perlengkapan K-3',
        payload_field: 'perlengkapan_k3',
        kode_kategori: 'PERALATAN_K3',
      },
    ],
  },
  'peralatan-to-parameter-ujis': {
    title: 'Master — Peralatan × parameter uji',
    description:
      'Mapping checklist kesiapan alat per parameter uji (metode sampling SNI, parameter, peralatan, dan section lain pada form). Checklist yang masih kosong tercentang otomatis dari mapping ini. Petugas, pengesahan, dan peta lokasi tetap diisi di pekerjaan.',
    apiPlural: 'peralatan-to-parameter-ujis',
    populate: '*',
    columns: [
      {
        field: 'parameter_uji',
        headerName: 'Parameter uji',
        flex: 0.4,
        valueGetter: (r) => parameterUjiCellLabel((r as { parameter_uji?: unknown }).parameter_uji),
      },
      {
        field: 'mapping_count',
        headerName: 'Item tercentang',
        flex: 0.15,
        valueGetter: (r) => {
          const row = r as Record<string, unknown>;
          const relationCount = PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS.reduce((sum, field) => {
            const value = row[field];
            return sum + (Array.isArray(value) ? value.length : 0);
          }, 0);
          const wadah =
            (String(row.jumlah_wadah_contoh_uji_air ?? '').trim() ? 1 : 0) +
            (String(row.jumlah_wadah_contoh_uji_padatan ?? '').trim() ? 1 : 0);
          return String(relationCount + wadah);
        },
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [],
  },
  'analis-type-data': {
    title: 'Master — Analis type',
    description: 'Master tipe analis. Dipakai di parameter uji dan di user admin analis lab.',
    apiPlural: 'analis-type-datas',
    populate: '*',
    columns: [
      { field: 'title', headerName: 'Title', flex: 0.5 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [{ attribute: 'title', kind: 'text', label: 'Title', required: true }],
  },
  'parameter-uji-analises': {
    title: 'Master — Parameter uji × analis',
    apiPlural: 'parameter-uji-analises',
    populate: '*',
    columns: [
      {
        field: 'jenis_pengukuran',
        headerName: 'Jenis pengukuran',
        flex: 0.3,
        valueGetter: (r) => stringifyCell((r as { jenis_pengukuran?: unknown }).jenis_pengukuran),
      },
      { field: 'analis_type', headerName: 'Analis type', flex: 0.3 },
      {
        field: 'analis_type_data',
        headerName: 'Analis type data',
        flex: 0.3,
        valueGetter: (r) => stringifyCell((r as { analis_type_data?: unknown }).analis_type_data),
      },
      {
        field: 'parameter_uji',
        headerName: 'Parameter uji',
        flex: 0.4,
        valueGetter: (r) => parameterUjiCellLabel((r as { parameter_uji?: unknown }).parameter_uji),
      },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'jenis_pengukuran_doc',
        kind: 'relation',
        relation: 'jenis_pengukuran',
        label: 'Jenis pengukuran',
      },
      { attribute: 'analis_type', kind: 'text', label: 'Analis type' },
      {
        attribute: 'analis_type_data_doc',
        kind: 'relation',
        relation: 'analis_type_data',
        label: 'Analis type data',
      },
      {
        attribute: 'parameter_uji_doc',
        kind: 'relation',
        relation: 'parameter_uji',
        label: 'Parameter uji',
        required: true,
      },
    ],
  },
  'form-questions': {
    title: 'Master — Form question',
    description:
      'Pertanyaan form sampling dikelompokkan per nama form. Jenis pengukuran diisi sekali per form. Nilai default terisi otomatis di selesai sampling dan bisa diubah petugas.',
    apiPlural: 'form-questions',
    populate: '*',
    createDefaults: { required: true, sort_order: 0 },
    columns: [
      { field: 'form_question_name', headerName: 'Nama form', flex: 0.3 },
      { field: 'title', headerName: 'Judul', flex: 0.35 },
      {
        field: 'jenis_pengukuran',
        headerName: 'Jenis pengukuran',
        flex: 0.45,
        minWidth: 220,
        valueGetter: (r) => {
          const v = (r as { jenis_pengukuran?: unknown }).jenis_pengukuran;
          if (Array.isArray(v)) {
            return v.map(stringifyCell).filter(Boolean).join('\n');
          }
          return stringifyCell(v);
        },
      },
      { field: 'answer_type', headerName: 'Tipe jawaban', flex: 0.15 },
      { field: 'default_value', headerName: 'Nilai default', flex: 0.2 },
      { field: 'sort_order', headerName: 'Urutan', flex: 0.08 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      { attribute: 'form_question_name', kind: 'text', label: 'Nama form', required: true },
      { attribute: 'title', kind: 'text', label: 'Judul pertanyaan', required: true },
      {
        attribute: 'jenis_pengukuran_docs',
        kind: 'relation_many',
        payload_field: 'jenis_pengukuran',
        label: 'Jenis pengukuran',
      },
      {
        attribute: 'answer_type',
        kind: 'enumeration',
        label: 'Tipe jawaban',
        options: ['text', 'number', 'decimal', 'boolean', 'single', 'multiple', 'date', 'file'],
        required: true,
      },
      { attribute: 'default_value', kind: 'text', label: 'Nilai default' },
      { attribute: 'required', kind: 'boolean', label: 'Wajib diisi' },
      { attribute: 'sort_order', kind: 'number', label: 'Urutan' },
    ],
  },
  'form-question-options': {
    title: 'Master — Form question option',
    description:
      'Opsi jawaban yang bisa dipakai banyak pertanyaan form sampling (mis. jam ke 1–12). Form question dipilih per form lewat checkbox. Tipe range mengisi kolom angka dari start sampai end.',
    apiPlural: 'form-question-options',
    populate: '*',
    columns: [
      { field: 'title', headerName: 'Judul opsi', flex: 0.35 },
      {
        field: 'form_question',
        headerName: 'Pertanyaan',
        flex: 0.35,
        valueGetter: (r) => {
          const v = (r as { form_question?: unknown }).form_question;
          if (Array.isArray(v)) {
            return v.map(stringifyCell).filter(Boolean).join('\n');
          }
          return stringifyCell(v);
        },
      },
      { field: 'answer_type', headerName: 'Tipe jawaban', flex: 0.15 },
      { field: 'start', headerName: 'Start', flex: 0.08 },
      { field: 'end', headerName: 'End', flex: 0.08 },
      { field: 'sort_order', headerName: 'Urutan', flex: 0.08 },
      { field: 'documentId', headerName: 'Id', flex: 0.45, minWidth: 220 },
    ],
    formFields: [
      {
        attribute: 'form_question_docs',
        kind: 'relation_many',
        payload_field: 'form_question',
        label: 'Form question',
        required: true,
      },
      { attribute: 'title', kind: 'text', label: 'Judul opsi', required: true },
      {
        attribute: 'answer_type',
        kind: 'enumeration',
        label: 'Tipe jawaban',
        options: ['text', 'number', 'decimal', 'boolean', 'single', 'multiple', 'range'],
      },
      {
        attribute: 'start',
        kind: 'number',
        label: 'Start',
        required: true,
        helperText: 'Inklusif. Contoh 1.',
        visibleWhen: { attribute: 'answer_type', equals: 'range' },
      },
      {
        attribute: 'end',
        kind: 'number',
        label: 'End',
        required: true,
        helperText: 'Inklusif. Contoh 12 menghasilkan kolom 1–12.',
        visibleWhen: { attribute: 'answer_type', equals: 'range' },
      },
      { attribute: 'sort_order', kind: 'number', label: 'Urutan' },
    ],
  },
};

export const MASTER_CRUD_SLUGS = Object.keys(MASTER_CRUD_REGISTRY);
